import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { DEFAULT_EMPLOYEE_COMMUTING_ENTRY } from "@/contexts/wizard/useScope3State";
import { calculateEmployeeCommutingEmissions } from "@/service/emissions";
import type { EmployeeCommutingEntry } from "@/contexts/wizard/types";

const modeLabels: Record<string, string> = {
  car_petrol_avg: "Car (Petrol)",
  car_diesel_avg: "Car (Diesel)",
  bus: "Bus",
  train_local: "Train / Metro",
  motorcycle: "Motorcycle",
  taxi: "Taxi",
};

interface Props {
  isOpen: boolean;
}

export default function EmployeeCommutingFormSection({ isOpen }: Props) {
  const {
    scope3Data,
    assessment,
    currentEmployeeCommutingEntry,
    setCurrentEmployeeCommutingEntry,
    addEmployeeCommutingEntry,
    updateEmployeeCommutingEntry,
    deleteEmployeeCommutingEntry,
    forceSave,
    isLocked,
    isReadOnly,
  } = useWizard();
  const { toast } = useToast();
  const disabled = isLocked || isReadOnly;
  const pendingSaveRef = useRef(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  const getModeLabel = (mode: string) => {
    return modeLabels[mode] ?? mode;
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentEmployeeCommutingEntry(DEFAULT_EMPLOYEE_COMMUTING_ENTRY);
      setEditingEntryId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      forceSave();
    }
  }, [scope3Data.employeeCommutingEntries, forceSave]);

  const passengerKmPreview = useMemo(() => {
    const distance = currentEmployeeCommutingEntry.distancePerDayKm || 0;
    const days = currentEmployeeCommutingEntry.daysPerWeek || 0;
    const weeks = currentEmployeeCommutingEntry.weeksPerYear || 0;
    const employees = currentEmployeeCommutingEntry.employeeCount || 0;
    return distance * days * weeks * employees;
  }, [
    currentEmployeeCommutingEntry.distancePerDayKm,
    currentEmployeeCommutingEntry.daysPerWeek,
    currentEmployeeCommutingEntry.weeksPerYear,
    currentEmployeeCommutingEntry.employeeCount,
  ]);

  const totalTco2e = useMemo(() => {
    return scope3Data.employeeCommutingEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0);
  }, [scope3Data.employeeCommutingEntries]);

  const completion = scope3Data.employeeCommutingEntries.length > 0 ? 100 : 0;

  const handleEditEntry = (entry: EmployeeCommutingEntry) => {
    setCurrentEmployeeCommutingEntry({
      mode: entry.mode,
      distancePerDayKm: entry.distancePerDayKm,
      daysPerWeek: entry.daysPerWeek,
      weeksPerYear: entry.weeksPerYear,
      employeeCount: entry.employeeCount,
      period: entry.period,
      // passengerKm is derived — NOT loaded from entry
    });
    setEditingEntryId(entry.id);
  };

  const handleCancelEdit = () => {
    setCurrentEmployeeCommutingEntry(DEFAULT_EMPLOYEE_COMMUTING_ENTRY);
    setEditingEntryId(null);
  };

  const handleUpdateEntry = async () => {
    const id = editingEntryId!;
    // Recompute derived passengerKm from current form values before dispatching
    const { distancePerDayKm = 0, daysPerWeek = 5, weeksPerYear = 48, employeeCount = 1 } = currentEmployeeCommutingEntry;
    const passengerKm = distancePerDayKm * daysPerWeek * weeksPerYear * employeeCount;

    updateEmployeeCommutingEntry(id, { ...currentEmployeeCommutingEntry, passengerKm, calculating: true });
    try {
      const tco2e = await calculateEmployeeCommutingEmissions({ ...currentEmployeeCommutingEntry, passengerKm });
      updateEmployeeCommutingEntry(id, { tco2e: tco2e ?? null, calculating: false });
      pendingSaveRef.current = true;
      setCurrentEmployeeCommutingEntry(DEFAULT_EMPLOYEE_COMMUTING_ENTRY);
      setEditingEntryId(null);
      toast({
        title: "Entry updated",
        description: tco2e != null ? `${tco2e.toFixed(3)} tCO₂e` : undefined,
      });
    } catch {
      updateEmployeeCommutingEntry(id, { calculating: false });
      toast({ title: "Failed to update entry", variant: "destructive" });
    }
  };

  const handleAddEntry = async () => {
    const result = await addEmployeeCommutingEntry();
    if (!result.ok) {
      toast({ title: result.error || "Unable to add commuting entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Commuting entry added",
      description: result.tco2e ? `${result.tco2e.toFixed(3)} tCO2e` : "Calculating...",
    });
  };

  return (
    <div className="space-y-6">
      {disabled && <LockedBanner />}

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">{"Add Commuting Entry"}</h4>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">{"Transport mode"}</Label>
              <Select
                value={currentEmployeeCommutingEntry.mode}
                onValueChange={(value) =>
                  setCurrentEmployeeCommutingEntry((prev) => ({
                    ...prev,
                    mode: value as typeof currentEmployeeCommutingEntry.mode,
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger className="form-select">
                  <SelectValue placeholder={"Select mode"} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(modeLabels).map(([value]) => (
                    <SelectItem key={value} value={value}>
                      {getModeLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Distance per day (km)"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="0.1"
                value={currentEmployeeCommutingEntry.distancePerDayKm ?? ""}
                onChange={(e) =>
                  setCurrentEmployeeCommutingEntry((prev) => ({
                    ...prev,
                    distancePerDayKm: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 15"}
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Days per week"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="1"
                value={currentEmployeeCommutingEntry.daysPerWeek ?? ""}
                onChange={(e) =>
                  setCurrentEmployeeCommutingEntry((prev) => ({
                    ...prev,
                    daysPerWeek: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 5"}
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Working weeks in reporting period"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="1"
                value={currentEmployeeCommutingEntry.weeksPerYear ?? ""}
                onChange={(e) =>
                  setCurrentEmployeeCommutingEntry((prev) => ({
                    ...prev,
                    weeksPerYear: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 48"}
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Number of employees"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="1"
                value={currentEmployeeCommutingEntry.employeeCount ?? ""}
                onChange={(e) =>
                  setCurrentEmployeeCommutingEntry((prev) => ({
                    ...prev,
                    employeeCount: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 20"}
                disabled={disabled}
              />
            </div>
            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentEmployeeCommutingEntry.period ?? null}
              onChange={(value) => setCurrentEmployeeCommutingEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />
          </div>

          <div className="rounded-[20px] border border-slate-100 bg-primary/10 px-4 py-3 text-sm leading-5 text-primary">
            {`Estimated passenger-km: ${passengerKmPreview.toFixed(1)} passenger-km`}
          </div>

          <div>
            {editingEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={handleAddEntry} disabled={disabled} className="form-add-button">
                <Plus className="mr-2 h-3 w-3" /> {"Add Entry"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-header">
          <h4 className="entries-title">{"Entries"}</h4>
          <span className="entries-count">
            {`${scope3Data.employeeCommutingEntries.length} entries`}
          </span>
        </div>

        {scope3Data.employeeCommutingEntries.length === 0 ? (
          <p className="entries-empty">{"No commuting entries yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-row-base">
                <TableHead className="table-head-base">{"Mode"}</TableHead>
                <TableHead className="table-head-base text-center">
                  {"Passenger-km"}
                </TableHead>
                <TableHead className="table-head-base text-center">
                  {"Employees"}
                </TableHead>
                <TableHead className="table-head-base text-center">{"Period"}</TableHead>
                <TableHead className="table-head-base text-center">{"tCO2e"}</TableHead>
                <TableHead className="table-head-base text-right">{"Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scope3Data.employeeCommutingEntries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className={`table-row-base${editingEntryId === entry.id ? " outline outline-2 outline-primary/30" : ""}`}
                >
                  <TableCell className="table-cell-base-strong">{getModeLabel(entry.mode)}</TableCell>
                  <TableCell className="table-cell-base-center">{entry.passengerKm.toFixed(1)}</TableCell>
                  <TableCell className="table-cell-base-center">{entry.employeeCount}</TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.calculating ? "Calculating..." : (entry.tco2e ?? 0).toFixed(3)}
                  </TableCell>
                  <TableCell className="table-cell-base-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingEntryId !== null && editingEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteEmployeeCommutingEntry(entry.id);
                        pendingSaveRef.current = true;
                      }}
                      className="delete-button"
                      disabled={disabled || editingEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="total-card">
        <p className="total-label">{"Total commuting emissions"}</p>
        <p className="total-value">{`${totalTco2e.toFixed(3)} tCO2e`}</p>
      </div>

      <p className="text-xs leading-4 text-slate-500">{`Completion: ${completion}%`}</p>
    </div>
  );
}
